import { Component, inject, signal } from "@angular/core";
import { DynamicDialogConfig, DynamicDialogRef } from "primeng/dynamicdialog";
import { WellExplorerService } from "../../services/well-explorer.service";
import { lastValueFrom } from "rxjs";
import { saveAs } from "file-saver-es";
import { UsersAdminService } from "../../services/users-admin.service";
import { AppNotificationService } from "src/app/shared/services/app-notification.service";
import { UserSessionsResult } from "./user-sessions.model";

interface SessionInfo {
  sessionId: string;
  designId: string;
  designName: string;
  lastModified: Date;
  isDeleted: boolean;
}

interface WellExplorerNode {
  id?: string;
  name?: string;
  children?: WellExplorerNode[];
}

@Component({
  selector: 'app-user-designs-dialog',
  template: `
    <div class="flex flex-col space-y-4">

      @if (isLoading()) {
        <div class="flex justify-center py-8">
          <p-progressSpinner strokeWidth="4" styleClass="w-12 h-12"></p-progressSpinner>
        </div>
      }

      @if (!isLoading() && sessions().length === 0) {
        <div class="text-center py-8 text-gray-500">
          No active sessions found for this user.
        </div>
      }

      @if (!isLoading() && sessions().length > 0) {
        <div class="flex flex-col space-y-2">
          <p-table 
            [value]="sessions()" 
            stripedRows
            selectionMode="multiple"
            [scrollable]="true" 
            scrollHeight="400px"
            styleClass="p-datatable-sm"
          >
            <ng-template pTemplate="header">
              <tr>
                <th>Session ID</th>
                <th>Design Name</th>
                <th>Design ID</th>
                <th>Last Modified</th>
                <th class="text-center" style="width: 120px">Actions</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-session>
              <tr>
                <td class="text-center"><span class="font-mono text-sm">{{ session.sessionId.substring(0, 8) }}...</span></td>
                <td class="text-center" >
                  {{ session.designName || 'Untitled' }}
                  @if (session.isDeleted) {
                    <span class="text-gray-500 italic"> (Deleted)</span>
                  }
                </td>
                <td class="text-center"><span class="font-mono text-xs">{{ session.designId.substring(0, 8) }}...</span></td>
                <td class="text-center">{{ session.lastModified | date:'short' }}</td>
                <td class="text-center">
                  <div class="flex justify-center space-x-1">
                    <p-button 
                      title="Copy design ID" 
                      size="small" 
                      [text]="true"
                      [disabled]="session.isDeleted"
                      icon="pi pi-copy" 
                      (onClick)="copyDesignId(session.designId)"
                    ></p-button>
                    <p-button 
                      title="Export design" 
                      size="small" 
                      [text]="true"
                      [disabled]="session.isDeleted"
                      icon="pi pi-download" 
                      (onClick)="exportDesign(session)"
                      [loading]="exportingDesignId() === session.designId"
                    ></p-button>
                  </div>
                </td>
              </tr>
            </ng-template>
          </p-table>
        </div>
      }

      <div class="flex justify-end pt-4">
        <p-button class="outlined-button" label="Close" (onClick)="close()"></p-button>
      </div>
    </div>
  `,
  standalone: false
})
export class UserDesignsDialogComponent {
  private readonly _dialogRef = inject(DynamicDialogRef);
  private readonly _dialogConfig = inject(DynamicDialogConfig);
  private readonly _usersAdminService = inject(UsersAdminService);
  private readonly _wellExplorerService = inject(WellExplorerService);
  private readonly _toaster = inject(AppNotificationService);

  readonly userName = signal<string>(this._dialogConfig.data.userName);
  readonly authId = signal<string>(this._dialogConfig.data.authId);
  readonly sessions = signal<SessionInfo[]>([]);
  readonly isLoading = signal<boolean>(true);
  readonly exportingDesignId = signal<string | null>(null);

  constructor() {
    this.loadUserSessions();
  }

  async loadUserSessions(): Promise<void> {
    this.isLoading.set(true);
    try {
      const [response, wellExplorer] = await Promise.all([
        lastValueFrom(this._usersAdminService.getUserSessions(this.authId())),
        lastValueFrom(this._wellExplorerService.getWellExplorer())
      ]) as [UserSessionsResult, WellExplorerNode];

      const designMap = this.buildDesignMap(wellExplorer);

      // Map sessions and filter to unique designs.
      // Some historical sessions can have null/empty app ids, so skip those safely.
      const sessionMap = new Map<string, SessionInfo>();
      const userSessions = response?.sessions ?? [];

      userSessions.forEach(session => {
        const currentAppIds = session?.currentAppIds;
        const designId = currentAppIds?.designId;

        if (!designId || sessionMap.has(designId)) {
          return;
        }

        const design = designMap.get(designId);
        sessionMap.set(designId, {
          sessionId: session.sessionId,
          designId,
          designName: design?.name ?? 'Unknown Design',
          lastModified: currentAppIds.lastUpdated ? new Date(currentAppIds.lastUpdated) : new Date(),
          isDeleted: !design
        });
      });

      this.sessions.set(Array.from(sessionMap.values()));
    } catch (error) {
      console.error('Failed to load user sessions:', error);
      this._toaster.showError('Failed to load user sessions');
      this.sessions.set([]);
    } finally {
      this.isLoading.set(false);
    }
  }

  copyDesignId(designId: string): void {
    navigator.clipboard.writeText(designId);
    this._toaster.showSuccess('Design ID copied to clipboard');
  }

  async exportDesign(session: SessionInfo): Promise<void> {
    this.exportingDesignId.set(session.designId);
    try {
      const wellExplorer = await lastValueFrom(this._wellExplorerService.getWellExplorer());
      const designMap = this.buildDesignMap(wellExplorer);
      const wellbore = designMap.get(session.designId)?.wellbore;
      
      if (!wellbore) {
        this._toaster.showError('Could not find wellbore for design');
        return;
      }

      const exportName = session.designName;
      const exportedDesign = await lastValueFrom(
        this._wellExplorerService.exportDesign(session.designId, wellbore.id, exportName)
      );

      const bytes = new TextEncoder().encode(JSON.stringify(exportedDesign));
      const blob = new Blob([bytes], { type: "application/json;charset=utf-8" });
      const exportDate = new Date();
      const fileDate = `${exportDate.getFullYear()}-${String(exportDate.getMonth() + 1).padStart(2, '0')}-${String(exportDate.getDate()).padStart(2, '0')}`;
      saveAs(blob, `${exportedDesign.design.name}_${fileDate}.json`);
      
      this._toaster.showSuccess('Design exported successfully');
    } catch (error) {
      console.error('Failed to export design:', error);
      this._toaster.showError('Failed to export design');
    } finally {
      this.exportingDesignId.set(null);
    }
  }

  private buildDesignMap(wellExplorer: WellExplorerNode): Map<string, { name: string; wellbore: WellExplorerNode }> {
    const designMap = new Map<string, { name: string; wellbore: WellExplorerNode }>();
    
    const traverse = (node: WellExplorerNode, path: WellExplorerNode[] = [], depth = 0): void => {
      // Design level is at depth 6: root(0) → company(1) → project(2) → site(3) → well(4) → wellbore(5) → design(6)
      if (node.id && depth === 6) {
        const wellbore = path[path.length - 1];
        designMap.set(node.id, { name: node.name ?? 'Untitled', wellbore });
      }
      
      node.children?.forEach(child => traverse(child, [...path, node], depth + 1));
    };
    
    traverse(wellExplorer);
    return designMap;
  }

  close(): void {
    this._dialogRef.close();
  }
}
